import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { KINDS, describeLive, loadBostonWeather, previewScene, readCachedWeather } from "./bostonWeather";

// Shares the weather between the location line, the sky behind the hero and the
// thrown skill icons. A visitor can preview other weather; ?weather=snow (and
// optionally &sky=night) opens the page already previewing it.
//
// Visitors whose system asks for reduced motion see the live weather as a still
// picture (`animate` is false). Picking a weather to preview is asking to see
// it, so that still plays.

const WeatherContext = createContext(null);

function readPreviewFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const kind = params.get("weather");
  if (!KINDS.includes(kind)) return null;
  const sky = params.get("sky");
  return { kind, sky: sky === "day" || sky === "night" ? sky : null };
}

function usePrefersReducedMotion() {
  const query = "(prefers-reduced-motion: reduce)";
  const [reduced, setReduced] = useState(() => window.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const media = window.matchMedia?.(query);
    if (!media) return undefined;
    const onChange = () => setReduced(media.matches);
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

// How long the weather line waits for a slow forecast before showing without it.
const SETTLE_MS = 1500;

export function WeatherProvider({ children }) {
  const [cached] = useState(readCachedWeather);
  const [liveData, setLiveData] = useState(cached);
  // Settled once the forecast has arrived, failed or taken too long. Until then
  // the line holds back its weather and menu, so they appear together instead of
  // the menu jumping aside when the forecast lands.
  const [settled, setSettled] = useState(cached !== null);
  const [preview, setPreviewState] = useState(readPreviewFromUrl);
  const reducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (cached) return undefined;
    let active = true;
    const timer = setTimeout(() => setSettled(true), SETTLE_MS);
    loadBostonWeather().then((data) => {
      if (!active) return;
      setLiveData(data);
      setSettled(true);
    });
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [cached]);

  // Keeps the address bar in step so a previewed weather can be shared as a link.
  const setPreview = useCallback((kind) => {
    setPreviewState(kind ? { kind, sky: null } : null);
    const url = new URL(window.location.href);
    url.searchParams.delete("sky");
    if (kind) url.searchParams.set("weather", kind);
    else url.searchParams.delete("weather");
    window.history.replaceState(window.history.state, "", url);
  }, []);

  const live = useMemo(() => (liveData ? { ...liveData, ...describeLive(liveData) } : null), [liveData]);

  const scene = useMemo(() => {
    if (preview) return previewScene(preview.kind, preview.sky, liveData);
    return live ? live.scene : null;
  }, [preview, live, liveData]);

  const animate = Boolean(scene) && (!reducedMotion || Boolean(preview));

  const value = useMemo(
    () => ({ live, settled, preview: preview?.kind ?? null, setPreview, scene, animate, reducedMotion }),
    [live, settled, preview, setPreview, scene, animate, reducedMotion]
  );

  return <WeatherContext.Provider value={value}>{children}</WeatherContext.Provider>;
}

// The weather for this page, or null outside a WeatherProvider.
export function useWeather() {
  return useContext(WeatherContext);
}
